import React, { useState } from "react";
import { Modal } from "@/components/Modal";
import { getGameIcon } from "@/lib/gameIcons";
import { GameDiagram } from "@/components/GameDiagram";
import { eraLabel } from "@/lib/util";
import { Heart, MapPin, Package, Users, Play } from "lucide-react";
import { PLAYABLE_BY_NAME } from "@/components/games/registry";

const Badge = ({ children, tone }) => (
  <span className={`rounded border-2 border-kirana-brown px-2 py-0.5 text-xs font-bold ${tone === "green" ? "bg-kirana-green text-white" : "bg-kirana-cream text-kirana-brown"}`}>
    {children}
  </span>
);

const InfoBlock = ({ icon: Icon, title, children }) => (
  <div className="rounded-md border-2 border-kirana-border bg-card p-3">
    <div className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase text-kirana-brown/70">
      <Icon className="h-3.5 w-3.5" /> {title}
    </div>
    {children}
  </div>
);

export const GameCard = ({ game, open, onClose, unlocked, onAdd, onPlay }) => {
  const [popped, setPopped] = useState(false);
  if (!game) return null;
  const Icon = getGameIcon(game.icon);
  const img = game.image_url || (game.images && game.images[0]);
  const playKey = PLAYABLE_BY_NAME[game.name];
  const handleAdd = () => {
    onAdd(game.id);
    setPopped(true);
    setTimeout(() => setPopped(false), 500);
  };

  return (
    <Modal open={open} onClose={onClose} testId="game-card" maxWidth="max-w-lg">
      <div className="p-5 sm:p-7">
        {/* memory scene */}
        <div
          className="relative mb-4 flex h-48 items-center justify-center overflow-hidden rounded-md border-[3px] border-kirana-brown"
          style={{ background: img ? "#FDF6E3" : `linear-gradient(145deg, ${game.placeholder_color}, ${game.placeholder_color}cc)` }}
        >
          {img ? (
            <img src={img} alt={game.name} className="h-full w-full object-contain" />
          ) : (
            <div className="flex flex-col items-center gap-2 text-white">
              <Icon className="h-12 w-12" strokeWidth={2} />
              <span className="font-signboard text-2xl font-bold drop-shadow">{game.name}</span>
              <span className="text-[10px] uppercase tracking-widest text-white/70">Memory scene · illustration coming</span>
            </div>
          )}
        </div>

        <h2 data-testid="game-card-name" className="font-signboard text-3xl font-bold leading-none text-kirana-brown">
          {game.name}
        </h2>
        {game.alt_names?.length > 0 && (
          <p className="mt-1 text-sm text-kirana-brown/60">a.k.a. {game.alt_names.join(" · ")}</p>
        )}
        <div className="mt-2 flex flex-wrap gap-2">
          {game.category && <Badge>{game.category}</Badge>}
          <Badge tone="green">{eraLabel(game.era_start)}</Badge>
        </div>

        {/* PLAY button on top */}
        {playKey && onPlay && (
          <button data-testid="game-play-btn" onClick={() => onPlay(playKey)}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-md border-[3px] border-kirana-brown bg-kirana-green px-5 py-3 text-lg font-bold text-white transition-transform hover:scale-[1.02] hard-shadow">
            <Play className="h-5 w-5 fill-white" /> PLAY GAME
          </button>
        )}

        <p className="mt-3 rounded-md border-2 border-dashed border-kirana-border bg-kirana-cream/60 p-3 text-sm italic text-kirana-brown">
          {game.memory_scene}
        </p>

        {game.diagram && <div className="mt-4"><GameDiagram type={game.diagram} /></div>}

        {/* how we played */}
        <h3 className="mt-4 font-signboard text-lg font-bold text-kirana-brown">How We Played</h3>
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {game.how_we_played.map((s, i) => (
            <div key={i} className="flex items-start gap-2 rounded-md border-2 border-kirana-border bg-card p-2">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 border-kirana-brown bg-kirana-gold text-xs font-bold text-kirana-brown">
                {i + 1}
              </span>
              <span className="text-sm text-kirana-brown">{s}</span>
            </div>
          ))}
        </div>

        {/* needed / where / players */}
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <InfoBlock icon={Package} title="What You Needed">
            <ul className="list-inside list-disc text-sm text-kirana-brown">
              {game.what_you_needed.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          </InfoBlock>
          <div className="space-y-3">
            <InfoBlock icon={MapPin} title="Where We Played">
              <p className="text-sm text-kirana-brown">{game.where_we_played}</p>
            </InfoBlock>
            {game.typical_players && (
              <InfoBlock icon={Users} title="Typical Players">
                <p className="text-sm text-kirana-brown">{game.typical_players}</p>
              </InfoBlock>
            )}
          </div>
        </div>

        {/* memory action */}
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          {unlocked ? (
            <div data-testid="game-memory-unlocked" className="flex items-center gap-2 rounded-md border-2 border-kirana-red bg-kirana-red/10 px-4 py-2 font-bold text-kirana-red">
              <Heart className={`h-5 w-5 fill-kirana-red ${popped ? "animate-heart-pop" : ""}`} /> MEMORY UNLOCKED
            </div>
          ) : (
            <button
              data-testid="game-memory-add-btn"
              onClick={handleAdd}
              className="flex items-center gap-2 rounded-md border-[3px] border-kirana-brown bg-kirana-red px-5 py-2.5 font-bold text-white transition-transform hover:scale-[1.03] hard-shadow"
            >
              <Heart className="h-5 w-5" /> Add to My Memories
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};
